import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, Clock3,
  LayoutList, Rows3, Users,
} from 'lucide-react';
import './calendar.css';

const WEEKDAYS = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
const SHORT_WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const TODAY = isoDate(new Date());

function localDate(value) {
  return new Date(`${value}T12:00:00`);
}

function isoDate(value) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function monthLabel(value) {
  const label = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' }).format(localDate(`${value}-01`));
  return (label.charAt(0).toUpperCase() + label.slice(1)).replace(' г.', '');
}

function gameCountLabel(value) {
  const mod100 = value % 100;
  const mod10 = value % 10;
  const word = mod100 >= 11 && mod100 <= 14 ? 'матчей' : mod10 === 1 ? 'матч' : mod10 >= 2 && mod10 <= 4 ? 'матча' : 'матчей';
  return `${value} ${word}`;
}

function fullDateLabel(value) {
  const label = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' }).format(localDate(value));
  return label.charAt(0).toUpperCase() + label.slice(1).replace(' г.', '');
}

function buildMonthDays(month) {
  const [year, monthNumber] = month.split('-').map(Number);
  const first = new Date(year, monthNumber - 1, 1, 12);
  const mondayOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, monthNumber, 0, 12).getDate();
  const cells = mondayOffset + daysInMonth <= 35 ? 35 : 42;
  const start = new Date(year, monthNumber - 1, 1 - mondayOffset, 12);
  return Array.from({ length: cells }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return { value: isoDate(date), day: date.getDate(), currentMonth: date.getMonth() === monthNumber - 1 };
  });
}

function buildSelectedWeek(selectedDate) {
  const selected = localDate(selectedDate);
  const mondayOffset = (selected.getDay() + 6) % 7;
  const monday = new Date(selected);
  monday.setDate(selected.getDate() - mondayOffset);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    return { value: isoDate(date), day: date.getDate(), weekday: SHORT_WEEKDAYS[index] };
  });
}

function CalendarAccessPanel({ data }) {
  return <aside className="calendar-access is-pro" aria-label="Открытый календарь">
    <CheckCircle2 aria-hidden="true" />
    <div><strong>Весь календарь открыт</strong><span>{gameCountLabel(data.leagueSeasonGameCount)} и фильтр по всем {data.teams.length} командам — бесплатно, без регистрации.</span></div>
  </aside>;
}

function TeamLogo({ abbr, logoUrl, size = 'compact' }) {
  return <span className={`calendar-team-logo is-${size}`} aria-hidden="true">
    <img src={logoUrl || `/media/teams/${abbr}.svg`} alt="" decoding="async" onError={(event) => { event.currentTarget.hidden = true; }} />
  </span>;
}

function CompactGame({ game }) {
  const washington = game.awayTeam === 'WSH' || game.homeTeam === 'WSH';
  return <span className={`calendar-cell-game${washington ? ' is-washington' : ''}`}>
    <span><TeamLogo abbr={game.awayTeam} logoUrl={game.awayLogoUrl} /><b>{game.awayTeam}</b></span>
    <small>@</small>
    <span><TeamLogo abbr={game.homeTeam} logoUrl={game.homeLogoUrl} /><b>{game.homeTeam}</b></span>
  </span>;
}

function MatchupRow({ game }) {
  const washington = game.awayTeam === 'WSH' || game.homeTeam === 'WSH';
  return <article className={`calendar-matchup${washington ? ' is-washington' : ''}`}>
    <div className="calendar-team away"><TeamLogo abbr={game.awayTeam} logoUrl={game.awayLogoUrl} size="matchup" /><div><small>Гости</small><strong>{game.awayTeam}</strong><span>{game.awayName}</span></div></div>
    <div className="calendar-matchup-center"><b>@</b><span><Clock3 aria-hidden="true" />{game.startTime ? <time dateTime={`${game.date}T${game.startTime}:00+03:00`}>{game.startTime} МСК</time> : 'Время не указано'}</span></div>
    <div className="calendar-team home"><TeamLogo abbr={game.homeTeam} logoUrl={game.homeLogoUrl} size="matchup" /><div><small>Хозяева</small><strong>{game.homeTeam}</strong><span>{game.homeName}</span></div></div>
  </article>;
}

function SelectedDaySchedule({ selectedDate, games, upcomingGames }) {
  const emptyTitle = 'В этот день матчей по выбранному фильтру нет';
  return <aside className="calendar-day-panel" aria-labelledby="selected-day-heading">
    <header><div><h2 id="selected-day-heading">{fullDateLabel(selectedDate)}</h2><p>{games.length ? `${games.length} ${games.length === 1 ? 'матч' : games.length < 5 ? 'матча' : 'матчей'}` : emptyTitle}</p></div><CalendarDays aria-hidden="true" /></header>
    <div className="calendar-day-content">
      <div className="calendar-matchup-list">
        {games.map((game) => <MatchupRow key={game.id} game={game} />)}
        {!games.length && <div className="calendar-empty-day"><CalendarDays aria-hidden="true" /><strong>{emptyTitle}</strong><span>Выберите другой день в календаре.</span></div>}
      </div>
      {upcomingGames.length > 0 && !games.length && <div className="calendar-upcoming">
        <h3>Ближайшие матчи месяца</h3>
        {upcomingGames.map((game) => <div key={game.id}><time>{new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' }).format(localDate(game.date))}</time><CompactGame game={game} /></div>)}
      </div>}
    </div>
  </aside>;
}

function MonthGrid({ days, gamesByDate, selectedDate, onSelectDate }) {
  const dayRefs = useRef([]);
  function onKeyDown(event, index) {
    const offsets = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    const offset = offsets[event.key];
    if (!offset) return;
    event.preventDefault();
    const nextIndex = Math.min(days.length - 1, Math.max(0, index + offset));
    dayRefs.current[nextIndex]?.focus();
    onSelectDate(days[nextIndex].value);
  }
  return <div className="calendar-grid-wrap">
    <div className="calendar-weekdays" aria-hidden="true">{WEEKDAYS.map((weekday) => <span key={weekday}>{weekday}</span>)}</div>
    <div className="calendar-month-grid" role="grid" aria-label="Календарь месяца">
      {days.map((day, index) => {
        const games = gamesByDate.get(day.value) || [];
        const selected = day.value === selectedDate;
        const today = day.value === TODAY;
        return <button
          type="button"
          role="gridcell"
          aria-selected={selected}
          aria-current={today ? 'date' : undefined}
          aria-label={`${fullDateLabel(day.value)}. ${games.length ? gameCountLabel(games.length) : 'Матчей нет'}`}
          className={`calendar-day-cell${day.currentMonth ? '' : ' is-outside'}${selected ? ' is-selected' : ''}${today ? ' is-today' : ''}`}
          key={day.value}
          ref={(element) => { dayRefs.current[index] = element; }}
          tabIndex={selected ? 0 : -1}
          onClick={() => onSelectDate(day.value)}
          onKeyDown={(event) => onKeyDown(event, index)}
        >
          <span className="calendar-day-number">{day.day}</span>
          <span className="calendar-cell-games">
            {games.slice(0, 3).map((game) => <CompactGame key={game.id} game={game} />)}
            {games.length > 3 && <small className="calendar-more-games">+ ещё {games.length - 3}</small>}
          </span>
        </button>;
      })}
    </div>
  </div>;
}

function CalendarList({ data, gamesByDate, onSelectDate }) {
  const days = [...gamesByDate.entries()].filter(([, games]) => games.length > 0);
  return <div className="calendar-list-view">
    {days.length ? days.map(([date, games]) => <section key={date}>
      <button type="button" onClick={() => onSelectDate(date)}><time>{fullDateLabel(date)}</time><span>{games.length} {games.length === 1 ? 'матч' : games.length < 5 ? 'матча' : 'матчей'} <ChevronRight /></span></button>
      <div>{games.map((game) => <MatchupRow key={game.id} game={game} />)}</div>
    </section>) : <div className="calendar-list-empty">В выбранном месяце матчей для текущего фильтра нет.</div>}
    <p className="calendar-list-summary">Показано {data.monthGameCount} из {data.seasonGameCount} доступных матчей сезона.</p>
  </div>;
}

export default function CalendarPage({ request }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const monthParam = params.get('month') || '';
  const teamParam = params.get('team') || '';
  const view = params.get('view') === 'list' ? 'list' : 'month';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Календарь матчей 2026/27 — OVI Stats';
    return () => { document.title = previousTitle; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const query = new URLSearchParams();
    if (monthParam) query.set('month', monthParam);
    if (teamParam) query.set('team', teamParam);
    request(`/calendar?${query}`).then((result) => { if (active) setData(result); }).catch((requestError) => { if (active) setError(requestError.message); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [monthParam, request, teamParam]);

  const gamesByDate = useMemo(() => {
    const map = new Map();
    for (const game of data?.games || []) {
      if (!map.has(game.date)) map.set(game.date, []);
      map.get(game.date).push(game);
    }
    return map;
  }, [data]);

  const selectedDateParam = params.get('date') || '';
  const selectedDate = data && selectedDateParam.startsWith(`${data.month}-`)
    ? selectedDateParam : data?.games[0]?.date || (data ? `${data.month}-01` : '');
  const selectedGames = selectedDate ? gamesByDate.get(selectedDate) || [] : [];
  const upcomingGames = data?.games.filter((game) => game.date > selectedDate).slice(0, 3) || [];
  const monthDays = useMemo(() => data ? buildMonthDays(data.month) : [], [data]);
  const selectedWeek = useMemo(() => selectedDate ? buildSelectedWeek(selectedDate) : [], [selectedDate]);

  function updateParams(changes) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) value ? next.set(key, value) : next.delete(key);
    setParams(next);
  }

  function selectDate(value) {
    const targetMonth = value.slice(0, 7);
    updateParams({ date: value, month: targetMonth === data.month ? data.month : targetMonth });
  }

  function moveMonth(direction) {
    const index = data.availableMonths.indexOf(data.month);
    const nextMonth = data.availableMonths[index + direction];
    if (!nextMonth) return;
    updateParams({ month: nextMonth, date: '' });
  }

  function goToNearestGames() {
    updateParams({ month: data.defaultMonth, date: '' });
  }

  const currentMonthIndex = data ? data.availableMonths.indexOf(data.month) : -1;

  return <div className="calendar-page">
    <div className="calendar-shell">
      <section className="calendar-intro">
        <div><h1>Календарь матчей</h1><p>Сезон {data?.season || '2026/27'} · время по Москве</p></div>
        {data && <CalendarAccessPanel data={data} />}
      </section>

      {error && <div className="calendar-error" role="alert">{error}</div>}
      {loading && !data && <div className="calendar-loading"><CalendarDays aria-hidden="true" /><strong>Загружаем календарь</strong></div>}

      {data && <>
        <section className="calendar-toolbar" aria-label="Управление календарём">
          <div className="calendar-month-controls">
            <button type="button" onClick={() => moveMonth(-1)} disabled={currentMonthIndex <= 0} aria-label="Предыдущий месяц"><ChevronLeft /></button>
            <strong aria-live="polite">{monthLabel(data.month)}</strong>
            <button type="button" onClick={() => moveMonth(1)} disabled={currentMonthIndex === data.availableMonths.length - 1} aria-label="Следующий месяц"><ChevronRight /></button>
          </div>
          <button type="button" className="calendar-nearest" onClick={goToNearestGames}><CalendarDays /> Ближайшие игры</button>
          <div className="calendar-view-switch" role="group" aria-label="Вид календаря">
            <button type="button" className={view === 'month' ? 'active' : ''} aria-pressed={view === 'month'} onClick={() => updateParams({ view: 'month' })}><Rows3 /> Месяц</button>
            <button type="button" className={view === 'list' ? 'active' : ''} aria-pressed={view === 'list'} onClick={() => updateParams({ view: 'list' })}><LayoutList /> Список</button>
          </div>
          <label className="calendar-team-filter">
            <span className="sr-only">Команда</span>
            <Users aria-hidden="true" /><select value={data.selectedTeam} onChange={(event) => updateParams({ team: event.target.value, date: '' })}><option value="">Все команды</option>{data.teams.map((team) => <option key={team.abbr} value={team.abbr}>{team.name}</option>)}</select>
          </label>
          <span className="calendar-result-count" aria-live="polite">{data.monthGameCount} {data.monthGameCount === 1 ? 'матч' : data.monthGameCount < 5 ? 'матча' : 'матчей'}</span>
        </section>

        <div className="calendar-mobile-day-rail" aria-label="Дни выбранной недели">
          {selectedWeek.map((day) => <button type="button" className={day.value === selectedDate ? 'active' : ''} key={day.value} onClick={() => selectDate(day.value)}><span>{day.weekday}</span><b>{day.day}</b><small>{(gamesByDate.get(day.value) || []).length || ''}</small></button>)}
        </div>

        <div className={`calendar-workspace is-${view}`}>
          <main className="calendar-primary">
            {view === 'month'
              ? <MonthGrid days={monthDays} gamesByDate={gamesByDate} selectedDate={selectedDate} onSelectDate={selectDate} />
              : <CalendarList data={data} gamesByDate={gamesByDate} onSelectDate={selectDate} />}
            <footer className="calendar-legend">
              <span><i /> Матчи Вашингтона</span><span><b>@</b> — команда слева играет в гостях</span><span><Clock3 /> Время указано по Москве</span>
            </footer>
          </main>
          <SelectedDaySchedule data={data} selectedDate={selectedDate} games={selectedGames} upcomingGames={upcomingGames} />
        </div>

      </>}
    </div>
  </div>;
}
